/// <reference path="../types/speech-recognition.d.ts" />
import { useCallback, useEffect, useRef, useState } from 'react'

import type { SpeechRecognitionError } from '../types/useSpeechRecognition'

interface UseSpeechRecognitionOptions {
  onInterimResult: (transcript: string) => void
  onFinalResult: (transcript: string) => void
  onError?: (error: SpeechRecognitionError) => void
  lang?: string
}

interface UseSpeechRecognitionReturn {
  isSupported: boolean
  isListening: boolean
  startListening: () => void
  stopListening: () => void
  toggleListening: () => void
  error: SpeechRecognitionError | null
}

const SpeechRecognitionCtor: SpeechRecognitionConstructor | undefined =
  typeof window !== 'undefined'
    ? (window.SpeechRecognition ?? window.webkitSpeechRecognition)
    : undefined

const isSupported = SpeechRecognitionCtor !== undefined

function mapError(errorCode: string): SpeechRecognitionError {
  switch (errorCode) {
    case 'not-allowed':
      return 'not-allowed'
    case 'no-speech':
      return 'no-speech'
    case 'network':
      return 'network'
    case 'aborted':
      return 'aborted'
    default:
      return 'unknown'
  }
}

const MAX_RESTARTS = 3
const RESTART_WINDOW_MS = 5000

export function useSpeechRecognition(
  options: UseSpeechRecognitionOptions,
): UseSpeechRecognitionReturn {
  const [isListening, setIsListening] = useState(false)
  const [error, setError] = useState<SpeechRecognitionError | null>(null)

  const recognitionRef = useRef<SpeechRecognition | null>(null)
  const isListeningRef = useRef(false)
  const optionsRef = useRef(options)
  optionsRef.current = options

  const restartTimestampsRef = useRef<number[]>([])

  const createRecognition = useCallback((): SpeechRecognition | null => {
    if (!SpeechRecognitionCtor) {
      return null
    }

    const recognition = new SpeechRecognitionCtor()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = optionsRef.current.lang ?? 'en-US'

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      // Ignore results that arrive after stopListening() was called
      if (!isListeningRef.current) {
        return
      }

      let interimTranscript = ''
      let finalTranscript = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (result.isFinal) {
          finalTranscript += result[0].transcript
        } else {
          interimTranscript += result[0].transcript
        }
      }

      // Process final before interim so the consumer can clear interimRef
      // before the new interim writes to it (avoids silent loss of interim text)
      if (finalTranscript) {
        optionsRef.current.onFinalResult(finalTranscript)
      }

      if (interimTranscript) {
        optionsRef.current.onInterimResult(interimTranscript)
      }
    }

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      const mapped = mapError(event.error)

      if (mapped === 'aborted' || mapped === 'no-speech') {
        return
      }

      setError(mapped)
      setIsListening(false)
      isListeningRef.current = false
      optionsRef.current.onError?.(mapped)
    }

    recognition.onend = () => {
      if (!isListeningRef.current) {
        return
      }

      // Auto-restart: browsers may stop after silence or a timeout
      const now = Date.now()
      const recent = restartTimestampsRef.current.filter(
        (t) => now - t < RESTART_WINDOW_MS,
      )
      restartTimestampsRef.current = recent

      if (recent.length >= MAX_RESTARTS) {
        setIsListening(false)
        isListeningRef.current = false
        return
      }

      restartTimestampsRef.current.push(now)
      try {
        recognition.start()
      } catch {
        setIsListening(false)
        isListeningRef.current = false
      }
    }

    return recognition
  }, [])

  const startListening = useCallback(() => {
    if (!isSupported || isListeningRef.current) {
      return
    }

    setError(null)
    restartTimestampsRef.current = []

    if (!recognitionRef.current) {
      recognitionRef.current = createRecognition()
    }

    if (!recognitionRef.current) {
      return
    }

    try {
      recognitionRef.current.start()
      setIsListening(true)
      isListeningRef.current = true
    } catch {
      setError('unknown')
    }
  }, [createRecognition])

  const stopListening = useCallback(() => {
    isListeningRef.current = false
    setIsListening(false)

    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop()
      } catch {
        // already stopped
      }
    }
  }, [])

  const toggleListening = useCallback(() => {
    if (isListeningRef.current) {
      stopListening()
    } else {
      startListening()
    }
  }, [startListening, stopListening])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isListeningRef.current = false
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort()
        } catch {
          // ignore
        }
        recognitionRef.current = null
      }
    }
  }, [])

  return {
    isSupported,
    isListening,
    startListening,
    stopListening,
    toggleListening,
    error,
  }
}
